import { useEffect, useRef } from 'react';
import { createTracker, type PageviewInput, type Tracker } from './tracker';

const PING_MS = 15_000;
const IDLE_MS = 60_000;

export type UseAnalyticsOptions = {
  endpoint: string;
  siteSlug: string;
  /** The page currently displayed. Changing it records a new pageview. */
  page: PageviewInput | null;
  enabled?: boolean;
};

/**
 * Engaged time, not wall clock: the interval only fires while the tab is
 * visible AND the reader has interacted within the last minute. A phone left
 * face-up on a table must not accumulate twenty minutes of "attention".
 */
export function useAnalytics({ endpoint, siteSlug, page, enabled = true }: UseAnalyticsOptions) {
  const ref = useRef<Tracker | null>(null);
  const lastInteraction = useRef(0);

  useEffect(() => {
    // SSR renders this on the worker, where there is no window at all.
    if (!enabled || typeof window === 'undefined') return;

    const tracker = createTracker({ endpoint, siteSlug });
    ref.current = tracker;
    lastInteraction.current = Date.now();

    const touch = () => {
      lastInteraction.current = Date.now();
    };
    const interactionEvents = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const;
    for (const e of interactionEvents) window.addEventListener(e, touch, { passive: true });

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      tracker.scroll(scrollable <= 0 ? 100 : (window.scrollY / scrollable) * 100);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastInteraction.current > IDLE_MS) return;
      tracker.ping();
    }, PING_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        tracker.ping();
        tracker.flush();
      }
    };
    const onPageHide = () => tracker.destroy();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      window.clearInterval(timer);
      for (const e of interactionEvents) window.removeEventListener(e, touch);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      tracker.destroy();
      ref.current = null;
    };
  }, [endpoint, siteSlug, enabled]);

  const path = page?.path;
  const articleId = page?.articleId;

  useEffect(() => {
    if (!page || !ref.current) return;
    ref.current.pageview({
      ...page,
      referrer: page.referrer ?? (typeof document === 'undefined' ? null : document.referrer) ?? null,
    });
    // The tracker is keyed on the page identity, not the object reference:
    // re-firing a pageview on every render would multiply every count.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, articleId]);
}
